/**
 * Body editor for the calc step: create-vs-into-existing target toggle,
 * target name (input or field slot), field type (create mode), the raw
 * expression box with inline validation, and the boolean-condition wizard.
 */
import React, { useState } from "react"
import { Button, Radio, Tooltip } from "antd"
import type { RadioChangeEvent } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTriangleExclamation,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons"
import { QueryBuilderStore } from "stores/queryBuilderStore"
import { CalcStep } from "types/queryBuilder.types"
import SingleFieldSlot from "../SingleFieldSlot.component"
import CalcWizard from "../CalcWizard/CalcWizard.component"
import { validateCalcExpression } from "../CalcWizard/calcExpression"
import { StepBodyProps } from "./stepBodyProps"

export interface CalcStepBodyProps extends StepBodyProps<CalcStep> {
  renameStep: QueryBuilderStore["renameTransformStep"]
  setMode: QueryBuilderStore["setStepMode"]
  setCalcFieldType: QueryBuilderStore["setCalcFieldType"]
  setCalcExpression: QueryBuilderStore["setCalcExpression"]
}

const CalcStepBody: React.FC<CalcStepBodyProps> = ({
  step,
  renameStep,
  setMode,
  setCalcFieldType,
  setCalcExpression,
}) => {
  const [calcWizardOpen, setCalcWizardOpen] = useState(false)

  return (
    <>
      <div className="qc-step__field-row">
        <span className="qc-step__field-label">Target:</span>
        <Radio.Group
          value={step.mode}
          onChange={(e: RadioChangeEvent) => setMode(step.id, e.target.value)}
          size="small"
        >
          <Radio.Button value="create">Create new field</Radio.Button>
          <Radio.Button value="into_existing">CALC into existing</Radio.Button>
        </Radio.Group>
      </div>
      {step.mode === "create" ? (
        <input
          className="qc-recodes__name qc-mono"
          type="text"
          value={step.target}
          onChange={(e) => renameStep(step.id, e.target.value)}
          placeholder="New field name (use // for nesting)"
          aria-label="Target field name"
        />
      ) : (
        <div className="qc-step__field-row">
          <span className="qc-step__field-label">Existing field:</span>
          <SingleFieldSlot
            value={step.target}
            onChange={(v) => renameStep(step.id, v)}
            placeholder="Drag the field to CALC into"
          />
        </div>
      )}
      {step.mode === "create" && (
        <div className="qc-step__field-row">
          <span className="qc-step__field-label">Type:</span>
          <Radio.Group
            value={step.fieldType}
            onChange={(e: RadioChangeEvent) =>
              setCalcFieldType(step.id, e.target.value)
            }
            size="small"
          >
            <Radio.Button value="INT">INT</Radio.Button>
            <Radio.Button value="REAL">REAL</Radio.Button>
            <Radio.Button value="BOOL">BOOL</Radio.Button>
          </Radio.Group>
        </div>
      )}
      <div className="qc-step__field-row">
        <span className="qc-step__field-label">Expression:</span>
        <input
          className="qc-recodes__name qc-mono"
          type="text"
          value={step.expression}
          onChange={(e) => setCalcExpression(step.id, e.target.value)}
          placeholder="e.g. <<a>> + <<b>>  or  <<age>> >= 18 AND <<age>> <= 35"
          aria-label="CALC expression"
          style={{ flex: 1 }}
        />
        <Tooltip title="Build a boolean condition visually" placement="top">
          <Button
            type="dashed"
            icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
            onClick={() => setCalcWizardOpen(true)}
            aria-label="Build expression with wizard"
          >
            Build…
          </Button>
        </Tooltip>
      </div>
      {step.expression.trim() &&
        !validateCalcExpression(step.expression).ok && (
          <div className="qc-calc__invalid">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {validateCalcExpression(step.expression).error}
          </div>
        )}
      <CalcWizard
        open={calcWizardOpen}
        initialExpression={step.expression}
        onApply={(expr) => setCalcExpression(step.id, expr)}
        onClose={() => setCalcWizardOpen(false)}
      />
    </>
  )
}

export default CalcStepBody
